import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "previews", "flash"]
  static values = {
    url: String,
    planUrl: String,
    maxVideoSize: Number,
    videosAllowed: Boolean,
  }

  connect() {
    this.queue = []
    this.failures = []
    this.discarded = 0
    this.uploading = false
    this.circumference = 2 * Math.PI * 21
  }

  select() {
    this.#clearFlash()
    const files = Array.from(this.inputTarget.files)
    const rejected = this.#rejectVideos(files)

    files
      .filter((file) => !rejected.includes(file))
      .forEach((file) => {
        const objectUrl = URL.createObjectURL(file)
        const previewEl = this.#createPreviewElement(file, objectUrl)
        this.previewsTarget.appendChild(previewEl)
        this.queue.push({ file, previewEl })
      })

    if (!this.uploading) this.#processQueue()
  }

  // Returns the files that must not be uploaded, having flagged why.
  #rejectVideos(files) {
    const videos = files.filter((file) => file.type.startsWith("video/"))
    if (videos.length === 0) return []

    if (this.hasVideosAllowedValue && !this.videosAllowedValue) {
      // The upgrade link only renders for the event owner, so guests see the
      // plain message — they cannot upgrade themselves.
      this.#showFlash("Videos sind im kostenlosen Tarif nicht verfügbar.", { upgrade: true })
      return videos
    }

    const oversized = videos.filter((file) => this.#exceedsVideoLimit(file))
    if (oversized.length > 0) {
      this.#showFlash(this.#oversizedMessage(oversized))
    }

    return oversized
  }

  #exceedsVideoLimit(file) {
    if (!this.hasMaxVideoSizeValue) return false

    return file.size > this.maxVideoSizeValue
  }

  #oversizedMessage(files) {
    const limit = Math.round(this.maxVideoSizeValue / (1024 * 1024))

    if (files.length === 1) {
      return `„${files[0].name}“ ist zu groß. Videos dürfen höchstens ${limit} MB haben.`
    } else {
      return `${files.length} Videos sind zu groß. Videos dürfen höchstens ${limit} MB haben.`
    }
  }

  #createPreviewElement(file, src) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative aspect-square rounded"

    const mediaElement = file.type.startsWith("video/")
      ? this.#createVideoPreview(src)
      : this.#createImagePreview(src)

    wrapper.appendChild(mediaElement)
    wrapper.appendChild(this.#createProgressOverlay())
    return wrapper
  }

  #createImagePreview(src) {
    const img = document.createElement("img")
    img.src = src
    img.className = "w-full aspect-square object-cover rounded brightness-50"
    return img
  }

  #createVideoPreview(src) {
    const video = document.createElement("video")
    video.src = src
    video.className = "w-full aspect-square object-cover rounded brightness-50"
    return video
  }

  #createProgressOverlay() {
    const svgNS = "http://www.w3.org/2000/svg"
    const svg = document.createElementNS(svgNS, "svg")
    svg.setAttribute(
      "class",
      "absolute inset-0 m-auto w-12 h-12 pointer-events-none",
    )
    svg.setAttribute("viewBox", "0 0 48 48")

    const bg = document.createElementNS(svgNS, "circle")
    bg.setAttribute("cx", "24")
    bg.setAttribute("cy", "24")
    bg.setAttribute("r", "21")
    bg.setAttribute("stroke", "#ffffff")
    bg.setAttribute("stroke-width", "6")
    bg.setAttribute("fill", "none")
    svg.appendChild(bg)

    const fg = document.createElementNS(svgNS, "circle")
    fg.setAttribute("class", "progress-ring__progress")
    fg.setAttribute("cx", "24")
    fg.setAttribute("cy", "24")
    fg.setAttribute("r", "21")
    fg.setAttribute("stroke", "#FF9670")
    fg.setAttribute("stroke-width", "6")
    fg.setAttribute("fill", "none")
    fg.style.strokeDasharray = `${this.circumference}`
    fg.style.strokeDashoffset = `${this.circumference}`
    fg.style.transition = "stroke-dashoffset 0.2s linear"
    svg.appendChild(fg)

    return svg
  }

  #setProgress(previewEl, percent) {
    const circle = previewEl.querySelector(".progress-ring__progress")
    const offset = this.circumference - (percent / 100) * this.circumference
    circle.style.strokeDashoffset = offset
  }

  async #processQueue() {
    if (this.queue.length === 0) {
      this.uploading = false
      this.#reportFailures()
      return
    }
    this.uploading = true

    const { file, previewEl } = this.queue.shift()
    try {
      await this.#uploadFile(file, previewEl)
    } catch (error) {
      this.failures.push({ file, error })
      // A plan failure rejects every remaining file too, so stop asking. Any
      // other failure is this one file's problem and the rest still go up.
      if (error.upgrade === true) this.#discardQueue()
    } finally {
      previewEl.remove()
      this.#processQueue()
    }
  }

  #discardQueue() {
    this.discarded += this.queue.length
    this.queue.forEach(({ previewEl }) => previewEl.remove())
    this.queue = []
  }

  // One message once the queue has drained, rather than one per file.
  #reportFailures() {
    if (this.failures.length === 0) return

    const reason = this.failures[0].error.message
    const upgrade = this.failures.some(({ error }) => error.upgrade === true)

    this.#showFlash(`${reason} ${this.#failureScope()}`, { upgrade })
    this.failures = []
    this.discarded = 0
  }

  #failureScope() {
    const affected = this.failures.length + this.discarded

    if (affected === 1) {
      return `Betroffen: „${this.failures[0].file.name}“.`
    } else {
      return `Betroffen: ${affected} Dateien.`
    }
  }

  #uploadFile(file, previewEl) {
    const formData = new FormData()
    const fieldName = file.type.startsWith("video/")
      ? "photo[video]"
      : "photo[image]"
    formData.append(fieldName, file)

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open("POST", this.urlValue)

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) xhr.setRequestHeader("X-CSRF-Token", csrfToken)
      xhr.setRequestHeader("Accept", "application/json")

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          const percent = (event.loaded / event.total) * 100
          this.#setProgress(previewEl, percent)
        }
      })

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this.#setProgress(previewEl, 100)
          resolve(xhr.response)
        } else {
          // Anything but a tagged JSON error is a hiccup on our side, so keep
          // the status around for support and tell the guest to try again.
          let errorMessage = "Upload fehlgeschlagen. Bitte versuche es erneut."
          let upgrade = false
          try {
            const data = JSON.parse(xhr.responseText)
            if (data.errors?.length) errorMessage = data.errors[0]
            upgrade = data.upgrade === true
          } catch (_) {}
          console.error(`Upload failed with status ${xhr.status}`, xhr.responseText)
          const error = new Error(errorMessage)
          error.upgrade = upgrade
          reject(error)
        }
      })

      xhr.addEventListener("error", () => reject(new Error("Verbindung unterbrochen. Bitte versuche es erneut.")))
      xhr.send(formData)
    })
  }

  #showFlash(message, { upgrade = false } = {}) {
    if (!this.hasFlashTarget) return
    const upgradeLink = upgrade && this.hasPlanUrlValue && this.planUrlValue
      ? ` <a href="${this.planUrlValue}" class="underline font-bold" data-turbo-frame="_top">Jetzt upgraden &rarr;</a>`
      : ""
    this.flashTarget.innerHTML = `
      <div class="rounded-md p-4 mb-4 font-medium ring-1 ring-inset ring-secondary text-secondary bg-secondary/10">
        <div class="flex items-start gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-5 shrink-0 mt-0.5">
            <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm font-semibold">${this.#escapeHtml(message)}${upgradeLink}</p>
        </div>
      </div>`
  }

  #clearFlash() {
    if (this.hasFlashTarget) this.flashTarget.innerHTML = ""
  }

  #escapeHtml(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
